import type { TranslateService } from '@ngx-translate/core';
import type { GridColumn } from '../components/kl-grid/grid.models';
import { formatKlDateValue } from './kl-date-format';

type ExportCellValue = string | number | boolean | Date | null | undefined | object;

function resolveExportCellValue(
  row: Record<string, ExportCellValue> | null | undefined,
  binding: string
): ExportCellValue {
  if (row == null || binding === '') return undefined;
  if (!binding.includes('.')) return row[binding];
  return binding.split('.').reduce<ExportCellValue>((acc, key) => {
    if (acc == null || typeof acc !== 'object' || acc instanceof Date) return undefined;
    return (acc as Record<string, ExportCellValue>)[key];
  }, row);
}

export interface ExportGridRowsToXlsxOptions {
  columns: GridColumn[];
  rows: object[];
  name: string;
  translate: TranslateService;
  onError?: (message: string) => void;
  /** Bindings summed into a bold totals row; no totals row when empty. */
  footerSumColumns?: readonly string[];
}

/** Sum of a column's numeric cells; numeric strings count too (some backend amounts arrive as strings). */
export function sumGridColumn(rows: object[], binding: string): number {
  return rows.reduce<number>((total, row) => {
    const raw = resolveExportCellValue(row as Record<string, ExportCellValue>, binding);
    const value = typeof raw === 'string' ? Number(raw) : raw;
    return typeof value === 'number' && Number.isFinite(value) ? total + value : total;
  }, 0);
}

/** Builds and downloads an xlsx file from grid column defs and row data. */
export function exportGridRowsToXlsx(options: ExportGridRowsToXlsxOptions): void {
  const { columns, rows, name, translate, onError, footerSumColumns = [] } = options;
  const cols = columns.filter((c) => !c.multi_select);
  const headers = cols.map((c) => (c.header ? translate.instant(c.header) : (c.binding ?? '')));

  const dataRows = rows.map((row) =>
    cols.map((col) => {
      const raw = resolveExportCellValue(row as Record<string, ExportCellValue>, col.binding ?? '');
      if (raw == null || raw === '') return '';
      if (col.format === 'date') return formatKlDateValue(raw, false);
      if (col.format === 'date-time') return formatKlDateValue(raw, true);
      if (col.translate) return translate.instant(String(raw));
      return raw;
    })
  );

  const footer = footerSumColumns.length
    ? cols.map((col) =>
        col.binding && footerSumColumns.includes(col.binding) ? sumGridColumn(rows, col.binding) : null
      )
    : undefined;

  Promise.all([import('xlsx-js-style'), import('./kl-xlsx-sheet')])
    .then(([mod, { formatXlsxSheet }]) => {
      // CJS package; production build can nest exports under `default`.
      const XLSX = mod.default ?? mod;
      const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
      formatXlsxSheet(
        ws,
        cols.map((col) => col.displayFractionDigits),
        footer
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, name);
      XLSX.writeFile(wb, `${new Date().toISOString().slice(0, 10)}-${name}.xlsx`);
    })
    .catch((err) => {
      console.error(err);
      onError?.('export_failed');
    });
}
